import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import KanbanBoard from "@/components/KanbanBoard";
import CandidateProfileDialog from "@/components/CandidateProfileDialog";
import AssignRecruitersDialog from "@/components/AssignRecruitersDialog";
import HealthWeightsDialog from "@/components/HealthWeightsDialog";
import BulkActionDialog from "@/components/BulkActionDialog";
import TransferJobDialog from "@/components/TransferJobDialog";
import { useDocsGateStageChange } from "@/lib/useDocsGateStageChange";
import {
    Briefcase, Flag, Calendar, Search, X, ChevronDown, ChevronRight, Users,
    Timer, Activity, Star, UserPlus, SlidersHorizontal, AlertTriangle, LayoutGrid, User,
    Sparkles, TrendingUp, ArrowRightLeft, CircleAlert, CircleCheck,
} from "lucide-react";

function daysUntil(iso) {
    if (!iso) return null;
    const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
    if (Number.isNaN(d.getTime())) return null;
    return Math.ceil((d.getTime() - Date.now()) / 86400000);
}

const COLLAPSE_KEY = "pw_pipeline_collapsed";
const RECRUITER_COLLAPSE_KEY = "pw_pipeline_recruiter_collapsed";
const GROUP_KEY = "pw_pipeline_group";
const FILTERS_KEY = "pw_pipeline_filters_v1";

// Load persisted filters from localStorage. Shape:
// { assigned_to_me: bool, priority: string[], job_ids: string[] }
function loadPersistedFilters() {
    try {
        const raw = localStorage.getItem(FILTERS_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        return {
            assigned_to_me: !!parsed.assigned_to_me,
            priority: Array.isArray(parsed.priority) ? parsed.priority : [],
            job_ids: Array.isArray(parsed.job_ids) ? parsed.job_ids : [],
        };
    } catch { return null; }
}
function persistFilters(f) {
    try {
        if (!f || (!f.assigned_to_me && !f.priority?.length && !f.job_ids?.length)) {
            localStorage.removeItem(FILTERS_KEY);
        } else {
            localStorage.setItem(FILTERS_KEY, JSON.stringify(f));
        }
    } catch { /* ignore */ }
}
function filtersActive(f) {
    return !!(f && (f.assigned_to_me || (f.priority?.length) || (f.job_ids?.length)));
}
function filtersToQuery(f) {
    if (!filtersActive(f)) return {};
    const q = {};
    if (f.assigned_to_me) q.assigned_to_me = 1;
    if (f.priority?.length) q.priority = f.priority.join(",");
    if (f.job_ids?.length) q.job_ids = f.job_ids.join(",");
    return q;
}

function healthTone(score) {
    if (score >= 70) return { border: "border-emerald-300", bg: "bg-emerald-50", text: "text-emerald-700", label: "Healthy" };
    if (score >= 40) return { border: "border-amber-300", bg: "bg-amber-50", text: "text-amber-700", label: "Watch" };
    return { border: "border-rose-300", bg: "bg-rose-50", text: "text-rose-700", label: "At risk" };
}

const COMPONENT_LABELS = { velocity: "Velocity", aging: "Stage aging", activity: "Activity", target: "Target", sla: "SLA" };

function HealthChip({ health, jobId }) {
    const tone = healthTone(health?.score ?? 0);
    return (
        <span className={`relative group/hc inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 cursor-default ${tone.border} ${tone.bg} ${tone.text}`} data-testid={`job-health-${jobId}`}>
            <Activity className="w-3 h-3" /> {health?.score ?? 0} · {tone.label}
            <span className="hidden group-hover/hc:block absolute z-[70] top-full left-0 mt-1 w-52 bg-black text-white p-3 shadow-xl normal-case tracking-normal font-sans">
                <span className="block text-[10px] font-bold uppercase tracking-widest mb-1.5">Health breakdown</span>
                {Object.entries(health?.components || {}).map(([k, v]) => (
                    <span key={k} className="flex items-center justify-between text-[11px] py-0.5">
                        <span className="text-white/70">{COMPONENT_LABELS[k] || k}</span>
                        <span>{v === null ? "n/a" : v}</span>
                    </span>
                ))}
            </span>
        </span>
    );
}

function ForecastChip({ forecast, jobId }) {
    if (!forecast || forecast.mode === "none") return null;
    let label, cls = "border-border text-foreground/80";
    if (forecast.mode === "filled") { label = "Filled"; cls = "border-emerald-300 bg-emerald-50 text-emerald-700"; }
    else if (forecast.eta_date) {
        const d = new Date(forecast.eta_date + "T00:00:00");
        label = `~${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} fill`;
        cls = "border-violet-300 bg-violet-50 text-violet-700";
    } else if (forecast.needs_more) {
        label = `Needs +${forecast.needs_more} cands`;
        cls = "border-amber-300 bg-amber-50 text-amber-700";
    } else return null;
    const tip = forecast.expected_hires !== undefined
        ? `Funnel model · expected ${forecast.expected_hires} hire(s) from current pipeline, ${forecast.remaining} opening(s) left`
        : `Velocity model · ${forecast.remaining} opening(s) left`;
    return (
        <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 inline-flex items-center gap-1 ${cls}`} title={tip} data-testid={`job-forecast-${jobId}`}>
            <TrendingUp className="w-3 h-3" /> {label}
        </span>
    );
}

const NBA_TONE = {
    high: { icon: CircleAlert, cls: "border-rose-200 bg-rose-50/70 text-rose-800", iconCls: "text-rose-600" },
    medium: { icon: CircleAlert, cls: "border-amber-200 bg-amber-50/70 text-amber-800", iconCls: "text-amber-600" },
    ok: { icon: CircleCheck, cls: "border-emerald-200 bg-emerald-50/50 text-emerald-800", iconCls: "text-emerald-600" },
};

// Hybrid Next-Best-Action: rule text renders instantly, sparkle swaps in an AI-polished line (server-cached).
function NbaRow({ job }) {
    const [text, setText] = useState(null);
    const [busy, setBusy] = useState(false);
    const nba = job.nba;
    if (!nba) return null;
    const tone = NBA_TONE[nba.severity] || NBA_TONE.ok;
    const Icon = tone.icon;
    const polish = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/pipeline/nba-ai", { job_id: job.id, code: nba.code, facts: nba.facts || {} });
            setText(data.text);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <div className={`flex items-center gap-2 px-4 py-1.5 border-b text-[12px] ${tone.cls}`} data-testid={`job-nba-${job.id}`}>
            <Icon className={`w-3.5 h-3.5 shrink-0 ${tone.iconCls}`} />
            <span className="font-mono text-[9px] uppercase tracking-widest opacity-60 shrink-0">Next:</span>
            <span className="truncate">{text || nba.text}</span>
            {!text && (
                <button
                    type="button"
                    onClick={polish}
                    disabled={busy}
                    className="ml-auto shrink-0 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest opacity-60 hover:opacity-100 transition-opacity"
                    title="Rephrase with AI"
                    data-testid={`job-nba-ai-${job.id}`}
                >
                    <Sparkles className={`w-3 h-3 ${busy ? "animate-pulse" : ""}`} /> {busy ? "…" : "AI"}
                </button>
            )}
        </div>
    );
}

function StageMiniBar({ apps, stages }) {
    const counts = useMemo(() => {
        const m = {};
        (apps || []).forEach((a) => { const k = a.stage || "Applied"; m[k] = (m[k] || 0) + 1; });
        return m;
    }, [apps]);
    const total = (apps || []).length;
    if (!total) return null;
    return (
        <div className="flex h-2 w-40 overflow-hidden border border-border" title={stages.map((s) => `${s.label}: ${counts[s.key] || 0}`).join(" · ")}>
            {stages.map((s) => {
                const n = counts[s.key] || 0;
                if (!n) return null;
                return <div key={s.key} style={{ width: `${(n / total) * 100}%`, backgroundColor: s.color || "#64748B" }} title={`${s.label}: ${n}`} />;
            })}
        </div>
    );
}

function TargetBar({ label, actual, target, money }) {
    const pct = Math.min(100, Math.round((actual / Math.max(1, target)) * 100));
    const fmt = (v) => money ? (v >= 1000 ? `${(v / 1000).toFixed(v % 1000 ? 1 : 0)}k` : v) : v;
    return (
        <div className="min-w-[110px]">
            <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-foreground/70">
                <span>{label}</span>
                <span className={pct >= 100 ? "text-emerald-700 font-bold" : ""}>{fmt(actual)}/{fmt(target)}</span>
            </div>
            <div className="h-1.5 bg-secondary border border-border mt-0.5">
                <div className={`h-full ${pct >= 100 ? "bg-emerald-500" : "bg-ikb"}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

function AssignmentTargets({ assignments }) {
    const withTargets = (assignments || []).filter((a) => Object.keys(a.targets || {}).length > 0);
    if (withTargets.length === 0) return null;
    return (
        <div className="px-4 py-2.5 border-b border-border bg-white flex flex-wrap gap-x-8 gap-y-2" data-testid="job-targets-row">
            {withTargets.map((a) => (
                <div key={a.user_id} className="flex items-end gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium pb-0.5">
                        {a.role === "lead" && <Star className="w-3 h-3 fill-amber-500 text-amber-500" />}
                        {a.name}
                    </span>
                    {Object.entries(a.targets).map(([k, t]) => (
                        <TargetBar key={k} label={k} actual={a.actuals?.[k] || 0} target={t} money={k === "revenue"} />
                    ))}
                </div>
            ))}
        </div>
    );
}

function JobSection({ j, stages, collapsed, onToggle, onAssign, onCardClick, onStageChange, onBulk, onTransfer }) {
    const dueIn = daysUntil(j.due_date);
    const overdue = dueIn !== null && dueIn < 0;
    const lead = (j.assignments || []).find((a) => a.role === "lead");
    const collabs = (j.assignments || []).filter((a) => a.role !== "lead");
    return (
        <section className="border border-border bg-white" data-testid={`pipeline-job-${j.id}`}>
            <header className="flex items-center gap-3 flex-wrap p-3.5 border-b border-border bg-secondary/40">
                <button type="button" onClick={onToggle} className="shrink-0 p-1 hover:bg-white border border-transparent hover:border-border transition-colors" data-testid={`job-collapse-${j.id}`} aria-label={collapsed ? "Expand" : "Collapse"}>
                    {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                <Link to={`/jobs/${j.id}`} className="flex items-center gap-2 min-w-0 group">
                    <Briefcase className="w-4 h-4 text-ikb shrink-0" />
                    <span className="font-display font-bold text-lg group-hover:text-ikb truncate">{j.title}</span>
                </Link>
                {(j.priority || "normal") !== "normal" && (
                    <span className="text-[10px] font-mono uppercase tracking-widest border border-amber-300 bg-amber-50 text-amber-700 px-2 py-0.5 inline-flex items-center gap-1"><Flag className="w-3 h-3" /> {j.priority}</span>
                )}
                <HealthChip health={j.health} jobId={j.id} />
                <span className="text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-0.5 inline-flex items-center gap-1 text-foreground/80" data-testid={`job-daysopen-${j.id}`}>
                    <Timer className="w-3 h-3" /> {j.days_open}d open
                </span>
                {j.sla_status && (
                    <span
                        className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 inline-flex items-center gap-1 ${j.sla_status === "breach" ? "border-rose-300 bg-rose-50 text-rose-700 font-bold" : j.sla_status === "warning" ? "border-amber-300 bg-amber-50 text-amber-700" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}
                        title={`${j.days_open}d of ${j.sla_days}d SLA`}
                        data-testid={`job-sla-${j.id}`}
                    >
                        SLA {j.sla_status === "breach" ? `breached +${j.days_open - j.sla_days}d` : `${Math.max(0, j.sla_days - j.days_open)}d left`}
                    </span>
                )}
                <ForecastChip forecast={j.forecast} jobId={j.id} />
                {dueIn !== null && (
                    <span className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 inline-flex items-center gap-1 ${overdue ? "border-rose-300 bg-rose-50 text-rose-700 font-bold" : "border-border text-foreground/80"}`}>
                        <Calendar className="w-3 h-3" /> {overdue ? `Overdue ${Math.abs(dueIn)}d` : `Due in ${dueIn}d`}
                    </span>
                )}
                {j.stale_count > 0 && (
                    <span className="text-[10px] font-mono uppercase tracking-widest border border-orange-300 bg-orange-50 text-orange-700 px-2 py-0.5 inline-flex items-center gap-1" title={`${j.stale_count} candidate(s) sitting 7+ days in the same stage`} data-testid={`job-stale-${j.id}`}>
                        <AlertTriangle className="w-3 h-3" /> {j.stale_count} stale
                    </span>
                )}
                <div className="ml-auto flex items-center gap-3 flex-wrap">
                    <StageMiniBar apps={j.applications} stages={stages} />
                    {(j.assignments || []).length > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-foreground/80" title={(j.assignments || []).map((a) => `${a.role === "lead" ? "★ " : ""}${a.name}`).join(", ")} data-testid={`job-assignees-${j.id}`}>
                            <Users className="w-3.5 h-3.5 text-ikb" />
                            {lead ? <span className="font-medium">{lead.name}</span> : null}
                            {collabs.length > 0 && <span className="text-foreground/60">+{collabs.length}</span>}
                        </span>
                    )}
                    <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/80">{j.applicant_count} cand.</span>
                    {onTransfer && (
                        <button
                            type="button"
                            onClick={onTransfer}
                            className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white hover:border-ikb hover:text-ikb px-2 py-1 inline-flex items-center gap-1 transition-colors"
                            data-testid={`job-transfer-${j.id}`}
                        >
                            <ArrowRightLeft className="w-3 h-3" /> Transfer
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={onAssign}
                        className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white hover:border-ikb hover:text-ikb px-2 py-1 inline-flex items-center gap-1 transition-colors"
                        data-testid={`job-assign-${j.id}`}
                    >
                        <UserPlus className="w-3 h-3" /> Assign
                    </button>
                </div>
            </header>
            <NbaRow job={j} />
            {!collapsed && <AssignmentTargets assignments={j.assignments} />}
            {!collapsed && (
                <div className="p-3">
                    {j.applications.length === 0 ? (
                        <div className="text-sm text-foreground/80 font-mono text-center py-6">No candidates yet</div>
                    ) : (
                        <KanbanBoard
                            apps={j.applications.map((a) => ({ ...a, _jobId: j.id }))}
                            stages={stages}
                            smartHover
                            agingHeat
                            bulkSelect
                            onBulkAction={(action, ids, extra) => onBulk(j, action, ids, extra)}
                            onCardClick={(a) => onCardClick(a)}
                            onStageChange={(appId, stage) => onStageChange(appId, stage, j.id)}
                            onPreviewProfile={(app) => onCardClick(app)}
                        />
                    )}
                </div>
            )}
        </section>
    );
}

export default function CombinedPipeline() {
    const [data, setData] = useState({ jobs: [], stages: [], recruiters: [], config: {} });
    const [loading, setLoading] = useState(true);
    const [profilePreview, setProfilePreview] = useState(null);
    const [q, setQ] = useState("");
    const [groupMode, setGroupMode] = useState(() => localStorage.getItem(GROUP_KEY) || "job");
    const [collapsed, setCollapsed] = useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem(COLLAPSE_KEY) || "[]")); } catch { return new Set(); }
    });
    // R23 — Recruiter-level collapse (only meaningful in groupMode='recruiter').
    const [recruiterCollapsed, setRecruiterCollapsed] = useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem(RECRUITER_COLLAPSE_KEY) || "[]")); } catch { return new Set(); }
    });
    const [assignJob, setAssignJob] = useState(null);
    const [weightsOpen, setWeightsOpen] = useState(false);
    const [bulk, setBulk] = useState(null);          // {action, ids, job}
    const [transfer, setTransfer] = useState(null);  // {job, fromId, fromName}
    // Filter state (persisted). Overload state comes from the API.
    const [filters, setFilters] = useState(() => loadPersistedFilters() || { assigned_to_me: false, priority: [], job_ids: [] });
    const [overload, setOverload] = useState(null);  // {counts, hint} — when set, we render the guard-rail
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [availableJobs, setAvailableJobs] = useState([]);  // {id, title} for the filter modal picker
    // Server-side saved filter presets (R19). Cross-device parity vs local-only defaults.
    const [presets, setPresets] = useState([]);
    const [hydratedFromPreset, setHydratedFromPreset] = useState(false);
    // Doc Freshness gate hook — warn/block on stage change past a gate stage
    const { changeStage: docsGateChangeStage, modal: docsGateModal } = useDocsGateStageChange();
    // R20 — "Fix this leak" deep-link from the weekly digest email.
    // URL: /pipeline?leak=<from>_to_<to>  →  banner + stage highlight + tracker.
    const [searchParams, setSearchParams] = useSearchParams();
    const leakParam = searchParams.get("leak");
    const leakSpec = useMemo(() => {
        if (!leakParam) return null;
        const parts = leakParam.split("_to_");
        if (parts.length !== 2) return null;
        return { from: parts[0].replace(/_/g, " "), to: parts[1].replace(/_/g, " "), raw: leakParam };
    }, [leakParam]);
    const dismissLeak = () => {
        const next = new URLSearchParams(searchParams);
        next.delete("leak");
        setSearchParams(next, { replace: true });
    };
    // R21 — Fire the click-through tracker exactly once per (user, leak, day).
    // Server dedupes via upsert; the ref guard just avoids redundant network noise.
    // Also fetch peer-count for the social-proof microcopy (R22).
    const leakTrackedRef = useRef(null);
    const [leakPeers, setLeakPeers] = useState(null);
    useEffect(() => {
        if (!leakSpec) { setLeakPeers(null); return; }
        if (leakTrackedRef.current === leakSpec.raw) return;
        leakTrackedRef.current = leakSpec.raw;
        api.post("/analytics/leak-click", {
            leak: leakSpec.raw,
            referrer: document.referrer || "",
        }).catch(() => { /* silent — analytics is best-effort */ });
        api.get("/analytics/leak-click-peers", { params: { leak: leakSpec.raw, days: 7 } })
            .then((r) => setLeakPeers(r.data))
            .catch(() => setLeakPeers(null));
    }, [leakSpec]);
    // R21 — Auto-scroll the first job's Kanban board to the "from" stage column
    // once data has loaded. Maps digest slug tokens → Kanban stage keys.
    // Runs once per leak param — subsequent scrolls happen only when the user
    // dismisses+re-navigates.
    const leakScrolledRef = useRef(null);
    useEffect(() => {
        if (!leakSpec || loading || overload) return;
        if (leakScrolledRef.current === leakSpec.raw) return;
        const SLUG_TO_STAGE = {
            applications: "Applied",
            application: "Applied",
            interviews: "Interview",
            interview: "Interview",
            offers: "Offer",
            offer: "Offer",
            hires: "Hired",
            hired: "Hired",
        };
        const target = SLUG_TO_STAGE[leakSpec.from.trim().toLowerCase()];
        if (!target) return;
        // Wait one tick for the boards to render.
        const t = setTimeout(() => {
            const col = document.querySelector(`[data-testid="kanban-col-${target}"]`);
            if (col) {
                col.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
                // Brief IKB pulse so recruiters see WHERE we landed them.
                col.classList.add("ring-2", "ring-ikb", "ring-offset-2");
                setTimeout(() => col.classList.remove("ring-2", "ring-ikb", "ring-offset-2"), 3000);
                leakScrolledRef.current = leakSpec.raw;
            }
        }, 300);
        return () => clearTimeout(t);
    }, [leakSpec, loading, overload, data]);

    const handleBulk = async (job, action, ids, extra) => {
        if (!ids.length) return;
        if (action === "move") {
            try {
                const { data } = await api.post("/applications/bulk-stage", { application_ids: ids, stage: extra.stage });
                toast.success(`${data.moved} candidate${data.moved !== 1 ? "s" : ""} moved to ${extra.stage}`);
                load(true);
            } catch (err) { toast.error(formatApiError(err)); }
        } else {
            setBulk({ action, ids, job });
        }
    };

    const load = async (silent = false, overrideFilters = null) => {
        if (!silent) setLoading(true);
        const activeFilters = overrideFilters ?? filters;
        const params = filtersToQuery(activeFilters);
        try {
            const { data } = await api.get("/pipeline/smart", { params });
            if (data?.overloaded) {
                setOverload({ counts: data.counts || {}, hint: data.hint || "" });
                setData({ jobs: [], stages: [], recruiters: [], config: {} });
                setFilterModalOpen(true);   // force the modal — the whole point of the guard-rail
            } else {
                setOverload(null);
                setData(data || { jobs: [], stages: [], recruiters: [], config: {} });
            }
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    // Fetch the (lightweight) active-job id/title list so the filter modal
    // can offer a picker even while the main pipeline is blocked by overload.
    useEffect(() => {
        api.get("/jobs", { params: { status: "active", include_skill_health: 0 } })
            .then((r) => setAvailableJobs((r.data || []).map((j) => ({ id: j.id, title: j.title }))))
            .catch(() => setAvailableJobs([]));
    }, []);

    // Load server-side presets — if the caller has a default preset AND local
    // filters are empty, hydrate from it. localStorage still wins if the user
    // already customised filters on this device.
    useEffect(() => {
        api.get("/pipeline/filter-presets")
            .then((r) => {
                const items = r.data?.items || [];
                setPresets(items);
                const def = items.find((p) => p.is_default);
                const localHas = filtersActive(filters);
                if (def && !localHas && !hydratedFromPreset) {
                    setHydratedFromPreset(true);
                    setFilters(def.filters);
                    load(true, def.filters);
                }
            })
            .catch(() => setPresets([]));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    const saveCurrentAsPreset = async (name, isDefault) => {
        try {
            const { data } = await api.post("/pipeline/filter-presets", {
                name, filters, is_default: !!isDefault,
            });
            setPresets((cur) => {
                const next = isDefault ? cur.map((p) => ({ ...p, is_default: false })) : cur;
                return [data, ...next.filter((p) => p.id !== data.id)];
            });
            toast.success(`Saved preset "${name}"`);
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const applyPreset = (preset) => {
        setFilters(preset.filters);
        persistFilters(preset.filters);
        setFilterModalOpen(false);
        load(false, preset.filters);
    };
    const setDefaultPreset = async (pid) => {
        try {
            await api.patch(`/pipeline/filter-presets/${pid}`, { is_default: true });
            setPresets((cur) => cur.map((p) => ({ ...p, is_default: p.id === pid })));
            toast.success("Default preset updated");
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const deletePreset = async (pid) => {
        try {
            await api.delete(`/pipeline/filter-presets/${pid}`);
            setPresets((cur) => cur.filter((p) => p.id !== pid));
            toast.success("Preset deleted");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const applyFilters = (next) => {
        setFilters(next);
        persistFilters(next);
        setFilterModalOpen(false);
        load(false, next);
    };
    const clearFilters = () => applyFilters({ assigned_to_me: false, priority: [], job_ids: [] });
    const forceLoadAll = async () => {
        // Explicit "load everything anyway" — user acknowledges the size.
        setFilterModalOpen(false);
        setLoading(true);
        try {
            const { data } = await api.get("/pipeline/smart", { params: { force: 1 } });
            setOverload(null);
            setData(data || { jobs: [], stages: [], recruiters: [], config: {} });
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    const toggleCollapse = (jid) => {
        setCollapsed((cur) => {
            const next = new Set(cur);
            if (next.has(jid)) next.delete(jid); else next.add(jid);
            localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next]));
            return next;
        });
    };
    const toggleRecruiterCollapse = (rid) => {
        setRecruiterCollapsed((cur) => {
            const next = new Set(cur);
            if (next.has(rid)) next.delete(rid); else next.add(rid);
            localStorage.setItem(RECRUITER_COLLAPSE_KEY, JSON.stringify([...next]));
            return next;
        });
    };
    // R23 — Bulk expand/collapse helpers used by the header toolbar.
    // Semantics:
    //   Expand All: expand every recruiter group AND every job section.
    //   Collapse All: collapse every recruiter group AND every job section.
    //   Expand to Recruiter: recruiters open, jobs closed (fast overview mode).
    //   Collapse to Recruiter: only meaningful in "By recruiter" — recruiters
    //     stay expanded, only their jobs get collapsed. Alias of Expand-to-Rec.
    // Data source: current jobs list + recruiterGroups (memo below).
    const persistJobs = (set) => localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...set]));
    const persistRec = (set) => localStorage.setItem(RECRUITER_COLLAPSE_KEY, JSON.stringify([...set]));
    const bulkExpandAll = () => {
        setCollapsed(() => { persistJobs(new Set()); return new Set(); });
        setRecruiterCollapsed(() => { persistRec(new Set()); return new Set(); });
    };
    const bulkCollapseAll = () => {
        const allJobIds = new Set((data.jobs || []).map((j) => j.id));
        setCollapsed(() => { persistJobs(allJobIds); return allJobIds; });
        const allRecIds = new Set(recruiterGroupsForBulk().map((g) => g.id));
        setRecruiterCollapsed(() => { persistRec(allRecIds); return allRecIds; });
    };
    const bulkCollapseToRecruiter = () => {
        // Recruiter groups expanded, but every job inside collapsed.
        const allJobIds = new Set((data.jobs || []).map((j) => j.id));
        setCollapsed(() => { persistJobs(allJobIds); return allJobIds; });
        setRecruiterCollapsed(() => { persistRec(new Set()); return new Set(); });
    };
    // Recompute the same recruiter grouping used for rendering, so the bulk
    // ops don't miss the "__unassigned" bucket when in job mode.
    const recruiterGroupsForBulk = () => {
        const ids = new Set();
        for (const j of (data.jobs || [])) {
            const as = j.assignments || [];
            if (as.length === 0) { ids.add("__unassigned"); continue; }
            for (const a of as) ids.add(a.user_id);
        }
        return [...ids].map((id) => ({ id }));
    };

    const setGroup = (mode) => { setGroupMode(mode); localStorage.setItem(GROUP_KEY, mode); };

    const updateStage = async (appId, stage, jobId) => {
        // Find the candidate for the docs-gate preflight
        let candId = null, candName = null;
        for (const j of data.jobs || []) {
            const a = (j.applications || []).find((x) => x.id === appId);
            if (a) {
                candId = a.candidate?.id || a.candidate_id;
                candName = a.candidate?.name || "";
                break;
            }
        }
        const res = await docsGateChangeStage(appId, stage, { candidate_id: candId, candidate_name: candName });
        if (res?.ok) {
            setData((cur) => ({
                ...cur,
                jobs: cur.jobs.map((j) => j.id !== jobId ? j : ({
                    ...j,
                    applications: j.applications.map((a) => a.id === appId ? { ...a, stage, stage_entered_at: new Date().toISOString() } : a),
                })),
            }));
        }
    };

    const jobs = useMemo(() => filteredJobs(data.jobs, q), [data.jobs, q]);

    const summary = useMemo(() => {
        const all = data.jobs || [];
        const cands = all.reduce((s, j) => s + (j.applicant_count || 0), 0);
        const breaches = all.filter((j) => j.sla_status === "breach").length;
        const atRisk = all.filter((j) => (j.health?.score ?? 100) < 40).length;
        const avg = all.length ? Math.round(all.reduce((s, j) => s + (j.health?.score || 0), 0) / all.length) : 0;
        return { jobs: all.length, cands, breaches, atRisk, avg };
    }, [data.jobs]);

    // Group jobs under each assigned recruiter (a job can appear under several)
    const recruiterGroups = useMemo(() => {
        if (groupMode !== "recruiter") return [];
        const map = new Map();
        const unassigned = [];
        for (const j of jobs) {
            const as = j.assignments || [];
            if (as.length === 0) { unassigned.push(j); continue; }
            for (const a of as) {
                if (!map.has(a.user_id)) map.set(a.user_id, { id: a.user_id, name: a.name, jobs: [], leadCount: 0, load: 0, targetHires: 0 });
                const g = map.get(a.user_id);
                g.jobs.push({ job: j, role: a.role, targets: a.targets, actuals: a.actuals });
                g.load += j.active_count || 0;
                g.targetHires += (a.targets?.hires || 0);
                if (a.role === "lead") g.leadCount += 1;
            }
        }
        const groups = [...map.values()].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        // Workload balance: flag anyone carrying > 2x the median active-candidate load
        const loads = groups.map((g) => g.load).sort((a, b) => a - b);
        const median = loads.length ? loads[Math.floor(loads.length / 2)] : 0;
        groups.forEach((g) => { g.overloaded = groups.length > 1 && median > 0 && g.load > 2 * median; });
        if (unassigned.length) groups.push({ id: "__unassigned", name: "Unassigned jobs", jobs: unassigned.map((j) => ({ job: j })), leadCount: 0, load: 0 });
        return groups;
    }, [jobs, groupMode]);

    const openProfile = (a) => setProfilePreview({ candidateId: a.candidate?.id || a.candidate_id, applicationId: a.id });

    return (
        <div className="max-w-screen-2xl mx-auto p-6 space-y-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">All openings</div>
                    <h1 className="font-display font-black text-4xl mt-2">Pipeline · command center</h1>
                    <p className="text-foreground/80 mt-2">Every active job with health, SLA, targets and recruiter ownership — drag candidates without leaving this page.</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex border border-border bg-white" data-testid="pipeline-group-toggle">
                        <button
                            type="button"
                            onClick={() => setGroup("job")}
                            className={`px-3 h-9 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 transition-colors ${groupMode === "job" ? "bg-ikb text-white" : "hover:text-ikb"}`}
                            data-testid="pipeline-group-job"
                        >
                            <LayoutGrid className="w-3.5 h-3.5" /> By job
                        </button>
                        <button
                            type="button"
                            onClick={() => setGroup("recruiter")}
                            className={`px-3 h-9 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 transition-colors ${groupMode === "recruiter" ? "bg-ikb text-white" : "hover:text-ikb"}`}
                            data-testid="pipeline-group-recruiter"
                        >
                            <User className="w-3.5 h-3.5" /> By recruiter
                        </button>
                    </div>
                    {/* R23 — bulk expand / collapse controls. Only appear when data is loaded. */}
                    {!overload && !loading && (data.jobs || []).length > 0 && (
                        <div className="inline-flex border border-border bg-white" data-testid="pipeline-expand-controls">
                            <button
                                type="button"
                                onClick={bulkExpandAll}
                                className="px-2 h-9 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 hover:text-ikb transition-colors border-r border-border"
                                data-testid="pipeline-expand-all"
                                title="Expand every job (and recruiter, if in By recruiter mode)"
                            >
                                <ChevronDown className="w-3.5 h-3.5" /> Expand all
                            </button>
                            {groupMode === "recruiter" && (
                                <button
                                    type="button"
                                    onClick={bulkCollapseToRecruiter}
                                    className="px-2 h-9 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 hover:text-ikb transition-colors border-r border-border"
                                    data-testid="pipeline-collapse-to-recruiter"
                                    title="Recruiter groups stay open, jobs inside collapse — fast overview"
                                >
                                    <ChevronRight className="w-3.5 h-3.5" /> To recruiter
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={bulkCollapseAll}
                                className="px-2 h-9 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1 hover:text-ikb transition-colors"
                                data-testid="pipeline-collapse-all"
                                title="Collapse everything"
                            >
                                <ChevronRight className="w-3.5 h-3.5" /> Collapse all
                            </button>
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={() => setFilterModalOpen(true)}
                        className={`h-9 px-3 border ${filtersActive(filters) ? "border-ikb bg-ikb/5 text-ikb" : "border-border bg-white hover:border-ikb hover:text-ikb"} text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 transition-colors`}
                        data-testid="pipeline-filters-btn"
                    >
                        <SlidersHorizontal className="w-3.5 h-3.5" />
                        {filtersActive(filters) ? "Filters ·" : "Filters"}
                        {filtersActive(filters) && (
                            <span className="tabular-nums">
                                {(filters.assigned_to_me ? 1 : 0) + (filters.priority?.length || 0) + (filters.job_ids?.length || 0)}
                            </span>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => setWeightsOpen(true)}
                        className="h-9 px-3 border border-border bg-white hover:border-ikb hover:text-ikb text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 transition-colors"
                        data-testid="pipeline-weights-btn"
                    >
                        <SlidersHorizontal className="w-3.5 h-3.5" /> Weights
                    </button>
                </div>
            </div>

            {/* Filter chip bar — reflect what's currently in effect */}
            {filtersActive(filters) && !overload && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="pipeline-filter-chips">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Filtering:</span>
                    {filters.assigned_to_me && (
                        <span className="text-[11px] font-mono border border-ikb bg-ikb/5 text-ikb px-2 py-0.5" data-testid="pipeline-chip-assigned">
                            Assigned to me
                        </span>
                    )}
                    {(filters.priority || []).map((p) => (
                        <span key={p} className="text-[11px] font-mono border border-ikb bg-ikb/5 text-ikb px-2 py-0.5" data-testid={`pipeline-chip-priority-${p}`}>
                            Priority: {p}
                        </span>
                    ))}
                    {(filters.job_ids || []).map((jid) => (
                        <span key={jid} className="text-[11px] font-mono border border-ikb bg-ikb/5 text-ikb px-2 py-0.5" data-testid={`pipeline-chip-job-${jid}`}>
                            Job: {availableJobs.find((j) => j.id === jid)?.title || jid.slice(0, 8)}
                        </span>
                    ))}
                    <button
                        type="button"
                        onClick={clearFilters}
                        className="text-[10px] font-mono uppercase tracking-widest text-rose-700 hover:underline"
                        data-testid="pipeline-clear-filters"
                    >
                        Clear all
                    </button>
                </div>
            )}

            {/* R20 — "Fix this leak" banner from weekly digest email deep-link */}
            {leakSpec && !overload && (
                <div className="border border-ikb bg-ikb/5 p-3 flex items-start gap-3" data-testid="pipeline-leak-banner">
                    <div className="text-lg leading-none pt-0.5">🎯</div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold">
                            Fix your leak: <span className="capitalize">{leakSpec.from}</span> → <span className="capitalize">{leakSpec.to}</span>
                        </div>
                        <div className="text-[11px] text-foreground/70 mt-0.5">
                            Focus on candidates stuck at the <span className="font-mono">{leakSpec.from}</span> stage — nudging them to the next step will close the leak flagged in your weekly digest.
                        </div>
                        {leakPeers && leakPeers.peer_count > 0 && (
                            <div
                                className="text-[11px] text-ikb mt-1 font-medium inline-flex items-center gap-1"
                                data-testid="pipeline-leak-peer-badge"
                                title={`${leakPeers.peer_count} teammate${leakPeers.peer_count === 1 ? "" : "s"} opened this same email in the last ${leakPeers.window_days} days`}
                            >
                                <Users className="w-3 h-3" />
                                You&apos;re one of {leakPeers.peer_count + 1} on your team acting on this leak this week
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={dismissLeak}
                        className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 hover:text-rose-700"
                        data-testid="pipeline-leak-banner-dismiss"
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {/* Overload guard-rail — replaces the board when unfiltered data is huge */}
            {overload && (
                <div className="border border-amber-300 bg-amber-50 p-6" data-testid="pipeline-overload-banner">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <h3 className="font-display font-bold text-lg leading-tight">Too much data to render</h3>
                            <p className="text-sm text-foreground/80 mt-1">{overload.hint}</p>
                            <div className="mt-3 text-[11px] font-mono grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-xl">
                                <div className="border border-amber-400 bg-white p-2">
                                    <div className="text-[9px] uppercase tracking-widest text-foreground/60">Active jobs</div>
                                    <div className="text-lg tabular-nums">{overload.counts?.jobs_active ?? "—"}</div>
                                </div>
                                <div className="border border-amber-400 bg-white p-2">
                                    <div className="text-[9px] uppercase tracking-widest text-foreground/60">Active apps</div>
                                    <div className="text-lg tabular-nums">{overload.counts?.applications_active ?? "—"}</div>
                                </div>
                                <div className="border border-border bg-white/50 p-2">
                                    <div className="text-[9px] uppercase tracking-widest text-foreground/60">Safe jobs limit</div>
                                    <div className="text-lg tabular-nums">{overload.counts?.threshold_jobs ?? "—"}</div>
                                </div>
                                <div className="border border-border bg-white/50 p-2">
                                    <div className="text-[9px] uppercase tracking-widest text-foreground/60">Safe apps limit</div>
                                    <div className="text-lg tabular-nums">{overload.counts?.threshold_applications ?? "—"}</div>
                                </div>
                            </div>
                            <div className="mt-4 flex items-center gap-2 flex-wrap">
                                <button
                                    type="button"
                                    onClick={() => setFilterModalOpen(true)}
                                    className="h-9 px-3 bg-ikb text-white text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 hover:bg-[#001f7a]"
                                    data-testid="pipeline-overload-open-filters"
                                >
                                    <SlidersHorizontal className="w-3.5 h-3.5" /> Choose a filter
                                </button>
                                <button
                                    type="button"
                                    onClick={forceLoadAll}
                                    className="h-9 px-3 border border-border bg-white text-[11px] font-mono uppercase tracking-widest hover:border-ikb hover:text-ikb"
                                    data-testid="pipeline-overload-force-load"
                                >
                                    Load anyway (slow)
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Board summary strip */}
            {!loading && !overload && data.jobs.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-5 border border-border bg-white divide-x divide-border" data-testid="pipeline-summary">
                    {[
                        ["Active jobs", summary.jobs, ""],
                        ["Candidates", summary.cands, ""],
                        ["Avg health", summary.avg, summary.avg >= 70 ? "text-emerald-700" : summary.avg >= 40 ? "text-amber-700" : "text-rose-700"],
                        ["SLA breaches", summary.breaches, summary.breaches ? "text-rose-700" : ""],
                        ["At-risk jobs", summary.atRisk, summary.atRisk ? "text-rose-700" : ""],
                    ].map(([label, val, cls]) => (
                        <div key={label} className="p-3">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">{label}</div>
                            <div className={`font-display font-black text-2xl mt-0.5 ${cls}`}>{val}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Filter bar */}
            <div className="flex items-center gap-2 border border-border bg-white px-3 py-2" data-testid="pipeline-filter-bar">
                <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Filter jobs or candidates — type a role, name, email…"
                    className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-foreground/50"
                    data-testid="pipeline-filter-input"
                />
                {q && (
                    <button type="button" onClick={() => setQ("")} className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-ikb inline-flex items-center gap-1" data-testid="pipeline-filter-clear">
                        Clear <X className="w-3 h-3" />
                    </button>
                )}
            </div>

            {loading ? (
                <div className="text-sm font-mono text-foreground/80">Loading...</div>
            ) : data.jobs.length === 0 ? (
                <div className="border border-dashed border-border bg-white p-16 text-center">
                    <h3 className="font-display font-bold text-xl">No active jobs</h3>
                    <p className="text-sm text-foreground/80 mt-2">Activate at least one job to see its pipeline here.</p>
                </div>
            ) : groupMode === "job" ? (
                <div className="space-y-6">
                    {jobs.map((j) => (
                        <JobSection
                            key={j.id}
                            j={j}
                            stages={data.stages}
                            collapsed={collapsed.has(j.id)}
                            onToggle={() => toggleCollapse(j.id)}
                            onAssign={() => setAssignJob(j)}
                            onCardClick={openProfile}
                            onStageChange={updateStage}
                            onBulk={handleBulk}
                        />
                    ))}
                </div>
            ) : (
                <div className="space-y-8">
                    {recruiterGroups.map((g) => {
                        const isRecCollapsed = recruiterCollapsed.has(g.id);
                        return (
                        <div key={g.id} data-testid={`pipeline-recruiter-group-${g.id}`}>
                            <div className="flex items-center gap-3 mb-3">
                                <button
                                    type="button"
                                    onClick={() => toggleRecruiterCollapse(g.id)}
                                    className="p-1 hover:bg-secondary/40 border border-transparent hover:border-border transition-colors"
                                    data-testid={`pipeline-recruiter-toggle-${g.id}`}
                                    aria-expanded={!isRecCollapsed}
                                    aria-label={isRecCollapsed ? "Expand recruiter" : "Collapse recruiter"}
                                >
                                    {isRecCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </button>
                                <div className="w-8 h-8 bg-ikb text-white flex items-center justify-center font-display font-bold text-sm shrink-0">
                                    {g.id === "__unassigned" ? "—" : (g.name || "?").slice(0, 1).toUpperCase()}
                                </div>
                                <h2 className="font-display font-bold text-xl">{g.name}</h2>
                                <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">
                                    {g.jobs.length} job{g.jobs.length === 1 ? "" : "s"}{g.leadCount ? ` · lead on ${g.leadCount}` : ""}{g.id !== "__unassigned" ? ` · ${g.load} active cand.` : ""}
                                </span>
                                {g.overloaded && (
                                    <span className="text-[10px] font-mono uppercase tracking-widest border border-rose-300 bg-rose-50 text-rose-700 px-2 py-0.5 inline-flex items-center gap-1" title="Carrying more than 2× the median load — consider transferring a job" data-testid={`recruiter-overloaded-${g.id}`}>
                                        <AlertTriangle className="w-3 h-3" /> Overloaded — rebalance?
                                    </span>
                                )}
                            </div>
                            {!isRecCollapsed && (
                                <div className="space-y-4 pl-2 border-l-2 border-ikb/20 ml-4">
                                    {g.jobs.map(({ job }) => (
                                        <JobSection
                                            key={`${g.id}-${job.id}`}
                                            j={job}
                                            stages={data.stages}
                                            collapsed={collapsed.has(job.id)}
                                            onToggle={() => toggleCollapse(job.id)}
                                            onAssign={() => setAssignJob(job)}
                                            onCardClick={openProfile}
                                            onStageChange={updateStage}
                                            onBulk={handleBulk}
                                            onTransfer={g.id !== "__unassigned" ? () => setTransfer({ job, fromId: g.id, fromName: g.name }) : undefined}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                        );
                    })}
                </div>
            )}

            {profilePreview && (
                <CandidateProfileDialog
                    open={!!profilePreview}
                    onClose={() => setProfilePreview(null)}
                    candidateId={profilePreview.candidateId}
                    applicationId={profilePreview.applicationId}
                />
            )}
            <AssignRecruitersDialog
                job={assignJob}
                recruiters={data.recruiters}
                defaultSla={data.config?.default_sla_days}
                open={!!assignJob}
                onClose={() => setAssignJob(null)}
                onSaved={() => load(true)}
            />
            <HealthWeightsDialog
                open={weightsOpen}
                onClose={() => setWeightsOpen(false)}
                config={data.config}
                onSaved={() => load(true)}
            />
            {bulk && (
                <BulkActionDialog
                    action={bulk.action}
                    ids={bulk.ids}
                    candidateIds={(bulk.job.applications || []).filter((a) => bulk.ids.includes(a.id)).map((a) => a.candidate_id)}
                    stages={data.stages}
                    onClose={() => setBulk(null)}
                    onDone={() => load(true)}
                />
            )}
            {transfer && (
                <TransferJobDialog
                    transfer={transfer}
                    recruiters={data.recruiters}
                    onClose={() => setTransfer(null)}
                    onSaved={() => load(true)}
                />
            )}
            {filterModalOpen && (
                <PipelineFilterModal
                    filters={filters}
                    availableJobs={availableJobs}
                    forceOpen={!!overload}
                    presets={presets}
                    onClose={() => setFilterModalOpen(false)}
                    onApply={applyFilters}
                    onClear={clearFilters}
                    onSavePreset={saveCurrentAsPreset}
                    onApplyPreset={applyPreset}
                    onSetDefaultPreset={setDefaultPreset}
                    onDeletePreset={deletePreset}
                />
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// PipelineFilterModal — force-opens when the pipeline is overloaded.
// Persists filters to localStorage via applyFilters so recruiters get their
// last used slice on the next visit.
// ═══════════════════════════════════════════════════════════════════════════
function PipelineFilterModal({ filters, availableJobs, forceOpen, onClose, onApply, onClear, presets = [], onSavePreset, onApplyPreset, onSetDefaultPreset, onDeletePreset }) {
    const [assignedToMe, setAssignedToMe] = useState(!!filters?.assigned_to_me);
    const [priority, setPriority] = useState(new Set(filters?.priority || []));
    const [jobIds, setJobIds] = useState(new Set(filters?.job_ids || []));
    const [jobSearch, setJobSearch] = useState("");
    const [presetName, setPresetName] = useState("");
    const [presetIsDefault, setPresetIsDefault] = useState(false);

    const togglePriority = (p) => setPriority((cur) => {
        const next = new Set(cur);
        if (next.has(p)) next.delete(p); else next.add(p);
        return next;
    });
    const toggleJob = (id) => setJobIds((cur) => {
        const next = new Set(cur);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const anySet = assignedToMe || priority.size > 0 || jobIds.size > 0;
    const jobList = useMemo(() => {
        const needle = jobSearch.trim().toLowerCase();
        return (availableJobs || []).filter((j) => !needle || (j.title || "").toLowerCase().includes(needle)).slice(0, 60);
    }, [availableJobs, jobSearch]);

    const submit = () => {
        onApply({
            assigned_to_me: assignedToMe,
            priority: [...priority],
            job_ids: [...jobIds],
        });
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget && !forceOpen) onClose(); }}
            data-testid="pipeline-filter-modal"
        >
            <div className="bg-white border border-border w-full max-w-lg max-h-[85vh] flex flex-col">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4 text-ikb" />
                    <span className="text-[11px] font-mono uppercase tracking-widest text-ikb">Filter pipeline</span>
                    {forceOpen && (
                        <span className="text-[10px] font-mono uppercase tracking-widest border border-amber-500 bg-amber-50 text-amber-800 px-1.5 py-0.5 ml-1">
                            Required
                        </span>
                    )}
                    {!forceOpen && (
                        <button type="button" onClick={onClose} className="ml-auto text-foreground/60 hover:text-foreground" data-testid="pipeline-filter-close" aria-label="Close">
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>

                <div className="p-4 space-y-4 overflow-y-auto">
                    {presets.length > 0 && (
                        <section data-testid="pipeline-filter-presets">
                            <div className="text-[11px] font-mono uppercase tracking-widest text-foreground/60 mb-2">Saved presets</div>
                            <div className="flex flex-col gap-1">
                                {presets.map((p) => (
                                    <div key={p.id} className="flex items-center gap-2 px-2 py-1.5 border border-border hover:border-ikb text-sm" data-testid={`pipeline-filter-preset-${p.id}`}>
                                        <button
                                            type="button"
                                            onClick={() => onApplyPreset?.(p)}
                                            className="text-left flex-1 min-w-0 truncate hover:text-ikb"
                                            data-testid={`pipeline-filter-preset-apply-${p.id}`}
                                        >
                                            {p.name}
                                            {p.is_default && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest border border-ikb bg-ikb/5 text-ikb px-1">Default</span>}
                                        </button>
                                        {!p.is_default && (
                                            <button
                                                type="button"
                                                onClick={() => onSetDefaultPreset?.(p.id)}
                                                className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 hover:text-ikb"
                                                data-testid={`pipeline-filter-preset-default-${p.id}`}
                                                title="Set as default (auto-applied on page load)"
                                            >
                                                Set default
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => onDeletePreset?.(p.id)}
                                            className="text-foreground/60 hover:text-rose-700"
                                            data-testid={`pipeline-filter-preset-delete-${p.id}`}
                                            title="Delete preset"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    <section>
                        <div className="text-[11px] font-mono uppercase tracking-widest text-foreground/60 mb-2">Ownership</div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                                type="checkbox"
                                checked={assignedToMe}
                                onChange={(e) => setAssignedToMe(e.target.checked)}
                                data-testid="pipeline-filter-assigned-to-me"
                            />
                            Only jobs assigned to me
                        </label>
                    </section>

                    <section>
                        <div className="text-[11px] font-mono uppercase tracking-widest text-foreground/60 mb-2">Priority</div>
                        <div className="flex items-center gap-2 flex-wrap">
                            {["low", "normal", "high", "urgent"].map((p) => {
                                const active = priority.has(p);
                                return (
                                    <button
                                        key={p}
                                        type="button"
                                        onClick={() => togglePriority(p)}
                                        className={`h-8 px-3 border text-[11px] font-mono uppercase tracking-widest transition-colors ${active ? "border-ikb bg-ikb text-white" : "border-border hover:border-ikb hover:text-ikb"}`}
                                        data-testid={`pipeline-filter-priority-${p}`}
                                    >
                                        {p}
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    <section>
                        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                            <div className="text-[11px] font-mono uppercase tracking-widest text-foreground/60">Specific jobs {jobIds.size > 0 && `· ${jobIds.size} picked`}</div>
                            <div className="flex items-center gap-2">
                                {jobList.length > 0 && !jobList.every((j) => jobIds.has(j.id)) && (
                                    <button
                                        type="button"
                                        onClick={() => setJobIds(new Set([...jobIds, ...jobList.map((j) => j.id)]))}
                                        className="text-[10px] font-mono uppercase tracking-widest text-ikb hover:underline"
                                        data-testid="pipeline-filter-select-all-jobs"
                                    >
                                        Select all visible ({jobList.length})
                                    </button>
                                )}
                                {jobIds.size > 0 && (
                                    <button type="button" onClick={() => setJobIds(new Set())} className="text-[10px] font-mono uppercase tracking-widest text-rose-700 hover:underline" data-testid="pipeline-filter-clear-jobs">
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                        <input
                            type="text"
                            value={jobSearch}
                            onChange={(e) => setJobSearch(e.target.value)}
                            placeholder="Search jobs by title…"
                            className="w-full h-9 px-3 border border-border text-sm"
                            data-testid="pipeline-filter-job-search"
                        />
                        <div className="mt-2 max-h-56 overflow-y-auto border border-border divide-y divide-border">
                            {jobList.length === 0 ? (
                                <div className="p-3 text-xs text-foreground/60">No active jobs match.</div>
                            ) : jobList.map((j) => {
                                const active = jobIds.has(j.id);
                                return (
                                    <label key={j.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-secondary/30" data-testid={`pipeline-filter-job-row-${j.id}`}>
                                        <input type="checkbox" checked={active} onChange={() => toggleJob(j.id)} />
                                        <span className="truncate flex-1 min-w-0">{j.title}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </section>
                </div>

                {onSavePreset && anySet && (
                    <div className="px-4 py-3 border-t border-border bg-secondary/20 flex items-center gap-2" data-testid="pipeline-filter-save-preset">
                        <input
                            type="text"
                            value={presetName}
                            onChange={(e) => setPresetName(e.target.value.slice(0, 60))}
                            placeholder="Save these filters as…"
                            className="h-8 px-2 border border-border text-sm flex-1 min-w-0"
                            data-testid="pipeline-filter-save-name"
                        />
                        <label className="flex items-center gap-1 text-[11px] font-mono text-foreground/70 whitespace-nowrap cursor-pointer">
                            <input
                                type="checkbox"
                                checked={presetIsDefault}
                                onChange={(e) => setPresetIsDefault(e.target.checked)}
                                data-testid="pipeline-filter-save-default"
                            />
                            Default
                        </label>
                        <button
                            type="button"
                            disabled={!presetName.trim()}
                            onClick={() => { onSavePreset(presetName.trim(), presetIsDefault); setPresetName(""); setPresetIsDefault(false); }}
                            className="h-8 px-3 border border-ikb text-ikb hover:bg-ikb hover:text-white text-[11px] font-mono uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="pipeline-filter-save-submit"
                        >
                            Save preset
                        </button>
                    </div>
                )}

                <div className="px-4 py-3 border-t border-border flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => { onClear(); onClose(); }}
                        className="h-9 px-3 border border-border text-[11px] font-mono uppercase tracking-widest hover:border-rose-500 hover:text-rose-700"
                        data-testid="pipeline-filter-clear-all"
                    >
                        Clear all
                    </button>
                    <div className="ml-auto flex items-center gap-2">
                        {!forceOpen && (
                            <button
                                type="button"
                                onClick={onClose}
                                className="h-9 px-3 border border-border text-[11px] font-mono uppercase tracking-widest hover:border-ikb hover:text-ikb"
                                data-testid="pipeline-filter-cancel"
                            >
                                Cancel
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={submit}
                            disabled={forceOpen && !anySet}
                            className="h-9 px-3 bg-ikb text-white text-[11px] font-mono uppercase tracking-widest hover:bg-[#001f7a] disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="pipeline-filter-apply"
                        >
                            Apply filter
                        </button>
                    </div>
                </div>
            </div>
            {docsGateModal}
        </div>
    );
}

// Free-text narrowing: matches job title + nested candidate name/email/title.
function filteredJobs(jobs, q) {
    const needle = (q || "").trim().toLowerCase();
    if (!needle) return jobs;
    return jobs
        .map((j) => {
            const jobMatch = (j.title || "").toLowerCase().includes(needle);
            const filteredApps = (j.applications || []).filter((a) => {
                const c = a.candidate || {};
                const hay = [c.name, c.email, c.current_title, c.current_company, a.stage]
                    .filter(Boolean).join(" ").toLowerCase();
                return hay.includes(needle);
            });
            if (jobMatch) return { ...j, applications: j.applications || [] };
            if (filteredApps.length > 0) return { ...j, applications: filteredApps, applicant_count: filteredApps.length };
            return null;
        })
        .filter(Boolean);
}
